/**
 * Light configuration schemas
 */

import { z } from "zod";
import { Color3Schema, Vector3Schema } from "./scene.schema";

/**
 * Base light configuration
 */
const BaseLightConfigSchema = z.object({
  diffuse: Color3Schema.optional(),
  intensity: z.number().min(0).optional(),
  name: z.string(),
  specular: Color3Schema.optional(),
});

/**
 * Hemispheric light configuration
 */
export const HemisphericLightConfigSchema = BaseLightConfigSchema.extend({
  direction: Vector3Schema.optional(),
  groundColor: Color3Schema.optional(),
  type: z.literal("hemispheric"),
});

/**
 * Directional light configuration
 */
export const DirectionalLightConfigSchema = BaseLightConfigSchema.extend({
  direction: Vector3Schema.optional(),
  position: Vector3Schema.optional(),
  shadowMaxZ: z.number().optional(),
  shadowMinZ: z.number().optional(),
  type: z.literal("directional"),
});

/**
 * Point light configuration
 */
export const PointLightConfigSchema = BaseLightConfigSchema.extend({
  position: Vector3Schema.optional(),
  range: z.number().positive().optional(),
  type: z.literal("point"),
});

/**
 * Spot light configuration
 */
export const SpotLightConfigSchema = BaseLightConfigSchema.extend({
  angle: z.number().min(0).max(Math.PI).optional(),
  direction: Vector3Schema.optional(),
  exponent: z.number().min(0).optional(),
  position: Vector3Schema.optional(),
  range: z.number().positive().optional(),
  type: z.literal("spot"),
});

/**
 * Light configuration discriminated union
 */
export const LightConfigSchema = z.discriminatedUnion("type", [
  HemisphericLightConfigSchema,
  DirectionalLightConfigSchema,
  PointLightConfigSchema,
  SpotLightConfigSchema,
]);

/**
 * Inferred types
 */
export type HemisphericLightConfig = z.infer<
  typeof HemisphericLightConfigSchema
>;
export type DirectionalLightConfig = z.infer<
  typeof DirectionalLightConfigSchema
>;
export type PointLightConfig = z.infer<typeof PointLightConfigSchema>;
export type SpotLightConfig = z.infer<typeof SpotLightConfigSchema>;
export type LightConfig = z.infer<typeof LightConfigSchema>;

/**
 * Type guards
 */
export function isHemisphericLightConfig(
  config: LightConfig,
): config is HemisphericLightConfig {
  return config.type === "hemispheric";
}

export function isDirectionalLightConfig(
  config: LightConfig,
): config is DirectionalLightConfig {
  return config.type === "directional";
}

export function isPointLightConfig(
  config: LightConfig,
): config is PointLightConfig {
  return config.type === "point";
}

export function isSpotLightConfig(
  config: LightConfig,
): config is SpotLightConfig {
  return config.type === "spot";
}

/**
 * Default configurations (separate from schemas as per CLAUDE.md)
 */
export const defaultHemisphericLightConfig: HemisphericLightConfig = {
  diffuse: { b: 1, g: 1, r: 1 },
  direction: { x: 0, y: 1, z: 0 },
  groundColor: { b: 0.2, g: 0.2, r: 0.2 },
  intensity: 0.7,
  name: "HemisphericLight",
  specular: { b: 0, g: 0, r: 0 },
  type: "hemispheric",
};

export const defaultDirectionalLightConfig: DirectionalLightConfig = {
  diffuse: { b: 1, g: 1, r: 1 },
  direction: { x: 0, y: -1, z: 0 },
  intensity: 1,
  name: "DirectionalLight",
  position: { x: 0, y: 10, z: 0 },
  shadowMaxZ: 100,
  shadowMinZ: 0.1,
  specular: { b: 1, g: 1, r: 1 },
  type: "directional",
};

export const defaultPointLightConfig: PointLightConfig = {
  diffuse: { b: 1, g: 1, r: 1 },
  intensity: 1,
  name: "PointLight",
  position: { x: 0, y: 10, z: 0 },
  range: 100,
  specular: { b: 1, g: 1, r: 1 },
  type: "point",
};

export const defaultSpotLightConfig: SpotLightConfig = {
  angle: Math.PI / 3,
  diffuse: { b: 1, g: 1, r: 1 },
  direction: { x: 0, y: -1, z: 0 },
  exponent: 2,
  intensity: 1,
  name: "SpotLight",
  position: { x: 0, y: 10, z: 0 },
  range: 100,
  specular: { b: 1, g: 1, r: 1 },
  type: "spot",
};

/**
 * Validation helpers
 */
export function validateLightConfig(input: unknown): LightConfig {
  return LightConfigSchema.parse(input);
}

export function safeValidateLightConfig(
  input: unknown,
):
  | { success: true; data: LightConfig }
  | { success: false; error: z.ZodError } {
  const result = LightConfigSchema.safeParse(input);
  if (result.success) {
    return { data: result.data, success: true };
  }
  return { error: result.error, success: false };
}
